import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { getSublocations, createSublocation, editSublocation } from '../../actions/sublocations';
import SearchableList from '../partials/SearchableList';
import SelectableListItem from '../partials/SelectableListItem';
import SublocationModal from './SublocationModal';
import SublocationDeleteModal from './SublocationDeleteModal';
import SublocationConfirmModal from './SublocationConfirmModal';
import SublocationFailedConfirmationFeedbackModal from './SublocationFailedConfirmationFeedbackModal';
import { destructServerErrors, allErrors } from '../../helpers/error';
import { formData, clearFormInputs } from '../../helpers/form';
import __ from '../../helpers/translation';
import localforage from "localforage";
import { reportStatus, useScheduledReportStore } from '../../lib/stores/scheduledReport';

const propTypes = {
  onSelected: PropTypes.func.isRequired,
  getSublocations: PropTypes.func.isRequired,
  createSublocation: PropTypes.func.isRequired,
  editSublocation: PropTypes.func.isRequired,
  selectedLocation: PropTypes.object.isRequired,
  currentItem: PropTypes.object
};

class SublocationSelect extends Component {

  constructor (props) {
    super(props);
    this.searchList = React.createRef();
    this.sublocations = [];
    this.currentItem = props.currentItem ? props.currentItem : null;
    this.onSelected = props.onSelected;
    this.template = this.template.bind(this);
    this.location = this.props.selectedLocation;

    this.deleteSublocation = this.deleteSublocation.bind(this);
    this.editSublocation = this.editSublocation.bind(this);
    this.loadSublocations = this.loadSublocations.bind(this);
    this.beforeConfirm = this.beforeConfirm.bind(this);

    this.state = {
      modal: {
        'errors': null,
        'loading': false
      },
      'deleteItem': null,
      'editItem': null,
      'confirmItem': null
    }
  }

  //#region Sublocation actions

    addSublocation(){
      this.setState({
        'editItem': null,
      }, () => {
        window.$('#modalAddSubLocation').modal('show');
      });
    }

    editSublocation(item){
      this.setState({
        'editItem': item
      }, () => {
        window.$('#listActions' + item.id).modal('hide');
        window.$('#modalAddSubLocation').modal('show');
      });
    }

    updateSublocation(form, data){
      this.props.editSublocation(this.location, this.state.editItem, data)
        .then(data => {
          this.searchList.current.setState(prevState => {
            let updateIndex = prevState.items
              .findIndex(sublocation => sublocation.id === data.sublocation.id);

            let newState = prevState;
            if(newState.items[updateIndex]){
              newState.items[updateIndex] = data.sublocation;
            }

            return newState
          });
          this.setState({
            'modal': {
              'loading': false,
              'errors': null
            },
            editItem: null
          });
          clearFormInputs(form);
        })
        .catch(error => {
            this.setState({
              'modal': {
                'errors': allErrors(destructServerErrors(error))
              }
            });
            return Promise.reject(error)
        });
    }

    deleteSublocation(item){
      this.setState({
        'deleteItem': item
      }, () => {
        window.$('#listActions' + item.id).modal('hide');
        window.$('#deleteSublocation').modal('show');
      });
    }

  //#endregion

  beforeConfirm(item){
    if(item.image_url){
      this.setState({
        'confirmItem': item,
      }, () => {
        window.$('#confirmSublocation').modal('show');
      });
      return;
    }

    return this.onSelected(item)
  }

  template(item){
    return <SelectableListItem 
      key={item.id?.toString()}
      id={item.id?.toString()}
      name={'locations'}
      title={item?.name}
      subtitle={item?.description}
      selected={this.currentItem && this.currentItem.id == item?.id}
      onSelect={(e) => this.beforeConfirm(item)}
      actions={
        <>
          {item.can_edit && (
            <a className="d-flex flex-row text-dark font-size-18 cur-p" onClick={(e) => this.editSublocation(item) }>
              <div className="icon-container">
                <i className="far fa-pencil"></i>
              </div>
              <p>{ __('app.general.edit') }</p>
            </a>
          )}
          {item.can_delete && (
            <a className="d-flex flex-row text-dark font-size-18 cur-p" onClick={(e) => this.deleteSublocation(item) }>
              <div className="icon-container">
                <i className="far fa-trash"></i>
              </div>
              <p>{ __('app.general.delete') }</p>
            </a>
          )}
        </> 
      }
    />;
  }

  /**
   * This method handles the form submission for the sublocation modal.
   * It will either create a new sublocation or update an existing
   * sublocation.
   * 
   * @param {Event} e The event object
   * @param {object} data The form data that is submitted 
   * @returns {Promise} The promise object that either resolves or rejects the request
   */
  handleModal(e, _data){
    e.preventDefault();

    this.setState({
      'modal': {
        'loading': true
      }
    });

    let form = e.target;
    let data = _data ?? formData(form);

    if(this.state.editItem){
      return this.updateSublocation(form, data);
    }

    this.props.createSublocation(this.location, data)
      .then(data => {
        this.searchList.current.setState(prevState => ({ 
            items: [
              ... prevState.items,
              data.sublocation
            ]
        }));
        this.setState({
          'modal': {
            'loading': false,
            'errors': null
          }
        });
        clearFormInputs(form);
      })
      .catch(error => {
          this.setState({
            'modal': {
              'errors': allErrors(destructServerErrors(error))
            }
          });
          return Promise.reject(error)
      });
  }

  async loadSublocations(){

    this.searchList.current.setState({ 
      loading: true
    });

    const { scheduledReports } = useScheduledReportStore.getState();

    if(window.navigator.onLine) {

        const { sublocations } = await this.props.getSublocations(this.location)
          .catch(error => {
            return Promise.reject(error)
          });

        // When the sublocation doesn't exist in the cached store, we show it
        const onlineSublocations = sublocations.filter(sublocation => {
          return !scheduledReports.some(report => report.data.relation_ids.sublocation_id == sublocation.id)
        })

        const filteredSublocations = sublocations.filter(sublocation => {
          return scheduledReports.some(report => report.data.relation_ids.sublocation_id == sublocation.id && report.status === reportStatus.INACTIVE)
        })   

        if(this.searchList.current) {
          this.searchList.current.setState({
            items: [...onlineSublocations, ...filteredSublocations],
            loading: false
          });
        }
    } 
    else {
      await localforage.setDriver(localforage.INDEXEDDB);
      const sublocations = await localforage.getItem('bs_sublocations_' + this.location.id);

      if(!sublocations) {
        this.searchList.current.setState({ 
          loading: false
        });
        return;
      }

      this.searchList.current.setState({
        items: sublocations.filter(sublocation => {
          return scheduledReports.some(report => report.data.relation_ids.sublocation_id == sublocation.id && report.status === reportStatus.INACTIVE)
        }),
        loading: false
      });

    }
  }

  componentDidMount () {
    this.loadSublocations()
  }

  render () {
    return (
      <>
        <h1 className="fw-600 d-inline-block">{ __('app.inspections.title_sublocation') }</h1>

        {(this.location.is_owner || this.location.can_create_sublocations) && (
          <i className="far fa-plus d-inline-block md-plus" onClick={() => this.addSublocation()}></i>
        )}

        <SearchableList
          ref={this.searchList}
          template={this.template}
          items={this.sublocations}
          searchField={'name'}
          loading={true}
        />

        <SublocationModal 
          modal={this.state.modal} 
          sublocation={this.state.editItem} 
          callback={(e) => this.handleModal(e)} 
        />

        <SublocationFailedConfirmationFeedbackModal />

        <SublocationConfirmModal 
          sublocation={this.state.confirmItem} 
          onConfirm={(e) => this.onSelected(this.state.confirmItem)} 
        />

        <SublocationDeleteModal 
          sublocation={this.state.deleteItem} 
          onDeleted={() => { 
            this.setState({
              deleteItem: null,
              editItem: null
            });
            this.loadSublocations();
          }} 
        />
      </>
    )
  }
}

SublocationSelect.propTypes = propTypes;

const mapDispatchToProps = {
  getSublocations,
  createSublocation,
  editSublocation
};

export default connect(null, mapDispatchToProps)(SublocationSelect);